import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { EmptyState, PageHeader, Skeleton } from "@/components/ui";
import { CompanyEdgeCard } from "@/components/utilities/company-edge-card";
import { UtilityCodeForm } from "@/components/utilities/utility-code-form";
import { VerifyEmailPanel } from "@/components/verify-email-panel";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import { useCompanyUtilities } from "@/hooks/use-company-utilities";
import {
  companyUtilityAccessErrorMessage,
  useRequestCompanyUtilityAccess,
} from "@/hooks/use-request-company-utility-access";
import { useCompanyStore } from "@/store";

export const Route = createFileRoute("/_company/company/utilities/")({
  component: UtilityRequestsPage,
});

const LOADING_ROWS = 2;
const EMAIL_NOT_VERIFIED_KEY = "company:utilities.request.errorEmailNotVerified";

/**
 * How a company gets into a water system: an admin types the Utility Code that
 * water system gave them, and the company appears in that water system's
 * access-request inbox. The minted edge is the whole effect — no roster
 * member earns a tester grant from the company's ask, and each person who
 * wants standing asks the utility themselves.
 *
 * The form sits ABOVE the relationships it creates, so the page reads as one
 * act and its consequence. The list's company verbs are the two the page
 * owns — the redemption above and the resubmit a standing curable rejection
 * carries in its row; a water system grants, pauses and ends an edge on its
 * own side — and it refreshes off the redemption's own invalidation.
 *
 * ADMIN-GATED, mirroring the server: the operation answers a `tester` 403
 * `not_company_admin`, so a `tester` sees the relationships and no form rather
 * than a control that can only be refused. The refusal is still MAPPED, because
 * a role can change under an open page.
 *
 * The 201 body's `org_name` is the ONLY thing this surface may ever say about
 * the water system behind a code. No refusal path names one, and no refusal
 * envelope on this operation carries one: the sole disclosure is the name of
 * the water system whose secret the caller typed correctly.
 */
function UtilityRequestsPage() {
  const { t } = useTranslation("company");
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const emailVerified = useCompanyStore((s) => s.user?.emailVerified);
  const utilities = useCompanyUtilities(companyId);
  const request = useRequestCompanyUtilityAccess(companyId);

  const failure = request.isError ? companyUtilityAccessErrorMessage(request.error) : null;
  // The mapper already classified the refusal, so the cure panel keys on its
  // verdict rather than re-reading the envelope.
  const unverified = failure?.key === EMAIL_NOT_VERIFIED_KEY;
  const minted = request.isSuccess ? request.data : null;

  return (
    <div className="space-y-6 p-8">
      <PageHeader
        title={t("utilities.request.pageTitle")}
        subtitle={t("utilities.request.pageSubtitle")}
      />

      {isAdmin && (
        <UtilityCodeForm
          title={t("utilities.request.title")}
          body={t("utilities.request.body")}
          label={t("utilities.request.label")}
          hint={t("utilities.request.hint")}
          submitLabel={t("utilities.request.submit")}
          onSubmit={(code) => request.mutate({ code })}
          isPending={request.isPending}
          result={
            minted === null ? undefined : (
              <p className="text-sm text-on-surface">
                {t("utilities.request.requested", { orgName: minted.org_name })}
              </p>
            )
          }
          errorMessage={
            failure === null
              ? null
              : failure.status === undefined
                ? t(failure.key)
                : t(failure.key, {
                    status: t(`utilities.request.statusLabels.${failure.status}`),
                  })
          }
          errorFooter={unverified && emailVerified !== false ? <VerifyEmailPanel /> : undefined}
        />
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-on-surface">{t("utilities.companyEdges")}</h2>
        {utilities.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: LOADING_ROWS }, (_, index) => (
              <Skeleton key={index} width="100%" height="20px" />
            ))}
          </div>
        ) : utilities.isError ? (
          <p role="status" className="text-sm text-muted">
            {t("utilities.loadError")}
          </p>
        ) : utilities.rows.length === 0 ? (
          <EmptyState
            variant="inline"
            title={t("utilities.noEdges.title")}
            description={t("utilities.noEdges.description")}
          />
        ) : (
          utilities.rows.map((edge) => (
            <CompanyEdgeCard
              key={edge.org_id}
              edge={edge}
              companyId={companyId}
              canResubmit={isAdmin}
            />
          ))
        )}
      </section>
    </div>
  );
}
